import React from 'react';

const UserIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const BuildingIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
    <line x1="8" y1="21" x2="8" y2="17"/>
    <line x1="16" y1="21" x2="16" y2="17"/>
    <line x1="12" y1="21" x2="12" y2="17"/>
  </svg>
);

const CheckCircleIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22,4 12,14.01 9,11.01"/>
  </svg>
);

const FeaturesSection = () => {
  const features = [
    {
      Icon: UserIcon,
      title: 'Lead & Client Management',
      desc: 'Track every prospect and client from initial inquiry through deal close. Never lose a lead or miss a follow-up again.',
      list: [
        'Centralized client profiles with full history',
        'Automated follow-up reminders',
        'Lead status tracking from intake to close',
        'Custom notes and deal annotations'
      ]
    },
    {
      Icon: BuildingIcon,
      title: 'Lender Matching & Submission',
      desc: 'Stop guessing which lender fits a deal. Our structured matching process guides brokers to the right lenders — and tracks every submission.',
      list: [
        'Structured lender eligibility criteria',
        'Submission tracking per lender',
        'Status updates on outstanding submissions',
        'Lender communication log'
      ]
    },
    {
      Icon: CheckCircleIcon,
      title: 'Deal Tracking & Pipeline',
      desc: 'A real-time view of every deal in your pipeline — at every stage. Know what needs attention today, and what\'s on track to close.',
      list: [
        'Visual pipeline board by deal stage',
        'Priority flags and next-action prompts',
        'Deal timeline and milestone tracking',
        'Reporting-ready deal summaries'
      ]
    }
  ];

  return (
    <section className="bg-white py-[90px] px-[5%]" id="features">
      <div className="text-center max-w-[860px] mx-auto">
        <div className="text-[0.78rem] font-bold uppercase tracking-[0.08em] text-blue-500 mb-3">
          Platform Features
        </div>
        <h2 className="text-[clamp(1.6rem,3.5vw,2.4rem)] font-black leading-tight mb-4 text-slate-900">
          Everything a broker needs,
          <br />
          in one intelligent platform
        </h2>
        <p className="text-[1.05rem] text-slate-600 max-w-[540px] mx-auto leading-[1.7] mb-[56px]">
          Designed for the way loan brokers actually work — from first contact to funded deal.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-[28px] mt-[56px]">
        {features.map((feature, index) => (
          <div key={index} className="border border-slate-200 rounded-[16px] p-9 hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(15,36,71,0.16)] transition-all relative overflow-hidden">
            <div className="w-[52px] h-[52px] rounded-[12px] bg-blue-50 flex items-center justify-center mb-[22px]">
              <feature.Icon className="w-6 h-6 text-blue-500" />
            </div>
            <h3 className="text-[1.15rem] font-bold text-slate-900 mb-[10px]">{feature.title}</h3>
            <p className="text-[0.92rem] text-slate-600 leading-[1.7] mb-[18px]">{feature.desc}</p>
            <ul className="space-y-2 m-0 p-0">
              {feature.list.map((item, i) => (
                <li key={i} className="text-[0.87rem] text-slate-600 flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-[1px] flex-shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="absolute top-0 left-0 right-0 h-px bg-blue-500" />
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
